import type { AppRouter } from "@repo/platform-api/trpc/routers/_app";
import {
  DOMAIN_ERROR_CODES,
  createCorrelationId,
  type DomainErrorCode,
} from "@repo/domain-events";
import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  httpSubscriptionLink,
  splitLink,
  type TRPCClient,
} from "@trpc/client";
import { EventSource as EventSourcePonyfill } from "eventsource";
import superjson from "superjson";
import { getPlatformApiBaseUrl } from "~/lib/env";
import {
  getAppAuthTransportAuthHeaders,
  getAppAuthTransportSnapshot,
  type AppAuthTransportSnapshot,
} from "~/lib/auth/app-auth-runtime";
import {
  toPlatformChainPreference,
  type VmId,
  type VmPreference,
} from "~/lib/auth/vm-types";
import { getRouteVisitId } from "~/lib/route-visit";
import {
  recordPlatformApiRequestFailure,
  recordPlatformApiResponseMetadata,
} from "~/lib/sentry";
import { trackTelemetryEvent } from "~/lib/telemetry";
import { redactUrlForTelemetry } from "~/lib/telemetry/privacy";
import type { TelemetryPlatformApiFailureKind } from "~/lib/telemetry";

type HeaderMap = Record<string, string>;

const PLATFORM_API_REQUEST_ABORTED_MESSAGE = "Platform API request aborted";

function createPlatformApiAbortError(): Error {
  if (typeof DOMException === "function") {
    return new DOMException(PLATFORM_API_REQUEST_ABORTED_MESSAGE, "AbortError");
  }

  const error = new Error(PLATFORM_API_REQUEST_ABORTED_MESSAGE);
  error.name = "AbortError";
  return error;
}

function createPlatformApiRequestTimeoutError(requestTimeoutMs: number): Error {
  return new Error(
    `Platform API request timed out after ${requestTimeoutMs}ms`,
  );
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

function getPlatformApiFailureKind(input: {
  errorMessage?: string;
  status?: number;
}): TelemetryPlatformApiFailureKind {
  if (input.status !== undefined) {
    return input.status >= 500 ? "server_error" : "client_error";
  }

  return input.errorMessage?.toLowerCase().includes("timed out")
    ? "timeout"
    : "network_error";
}

function getPlatformApiErrorCode(
  kind: TelemetryPlatformApiFailureKind,
): DomainErrorCode {
  switch (kind) {
    case "client_error":
      return DOMAIN_ERROR_CODES.platformApiClientError;
    case "server_error":
      return DOMAIN_ERROR_CODES.platformApiServerError;
    case "timeout":
      return DOMAIN_ERROR_CODES.platformApiTimeout;
    case "network_error":
      return DOMAIN_ERROR_CODES.platformApiNetworkError;
  }
}

function trackPlatformApiFailure(input: {
  correlationId?: string | null;
  errorMessage?: string;
  requestId?: string | null;
  routeVisitId: string;
  status?: number;
  url: string;
}): void {
  const failureKind = getPlatformApiFailureKind({
    errorMessage: input.errorMessage,
    status: input.status,
  });
  const errorCode = getPlatformApiErrorCode(failureKind);
  trackTelemetryEvent("platform_api.request.failed", {
    error_code: errorCode,
    failure_kind: failureKind,
    route_visit_id: input.routeVisitId,
    status: input.status ?? null,
    url_path: redactUrlForTelemetry(input.url),
  });
}

function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.url;
  }

  return input.toString();
}

function getEventSourceConstructor() {
  if (typeof globalThis.EventSource === "function") {
    return EventSourcePonyfill;
  }

  return EventSourcePonyfill;
}

export type PlatformApiTrpcClient = TRPCClient<AppRouter>;

const PLATFORM_API_TRPC_MAX_URL_LENGTH = 2080;

/**
 * Procedure namespaces that must never share an HTTP request with anything
 * else. httpBatchLink coalesces concurrent calls into one response, so a
 * batch resolves only when its slowest member does — search fires several
 * lanes at once with very different budgets (the local lanes answer in
 * ~100ms, the Cloudflare AI Search lane takes ~1.5s), and batching made the
 * fast rows wait for the slowest. Rate limiting is a per-procedure tRPC
 * middleware, so splitting these out costs no extra quota.
 */
const OUT_OF_BAND_PROCEDURE_PREFIXES = ["assetSelector."] as const;

export function isOutOfBandProcedurePath(path: string): boolean {
  return OUT_OF_BAND_PROCEDURE_PREFIXES.some((prefix) =>
    path.startsWith(prefix),
  );
}

const RESERVED_REACT_PROXY_KEYS = new Set<string>([
  "$$typeof",
  "__esModule",
  "constructor",
  "displayName",
  "prototype",
  "then",
]);

function wrapTrpcClientForReact(
  client: PlatformApiTrpcClient,
): PlatformApiTrpcClient {
  const cache = new WeakMap<object, object>();

  const wrapNode = <T>(value: T): T => {
    if (
      (typeof value !== "object" || value === null) &&
      typeof value !== "function"
    ) {
      return value;
    }

    const target = value as object;
    const cached = cache.get(target);
    if (cached) {
      return cached as T;
    }

    const wrapped = new Proxy(target, {
      get(innerTarget, property, receiver) {
        if (
          typeof property === "string" &&
          RESERVED_REACT_PROXY_KEYS.has(property)
        ) {
          return undefined;
        }

        if (property === "toJSON") {
          return () => "tRPC.proxy(client)";
        }

        if (property === "toString") {
          return () => "tRPC.proxy(client)";
        }

        if (property === "valueOf") {
          return () => "tRPC.proxy(client)";
        }

        if (
          property === Symbol.iterator ||
          property === Symbol.asyncIterator ||
          property === Symbol.toPrimitive
        ) {
          return undefined;
        }

        return wrapNode(Reflect.get(innerTarget, property, receiver));
      },
    });

    cache.set(target, wrapped);
    return wrapped as T;
  };

  return wrapNode(client);
}

export type EditorSnapshotQueryInput = Parameters<
  PlatformApiTrpcClient["editor"]["getSnapshot"]["query"]
>[0];

export type EditorSnapshotQueryOutput = Awaited<
  ReturnType<PlatformApiTrpcClient["editor"]["getSnapshot"]["query"]>
>;

export type EditorSearchForAssetsInput = Parameters<
  PlatformApiTrpcClient["assets"]["searchForEditor"]["query"]
>[0];

export type EditorSearchForAssetsOutput = Awaited<
  ReturnType<PlatformApiTrpcClient["assets"]["searchForEditor"]["query"]>
>;

export type EditorAssetSuggestionsInput = Parameters<
  PlatformApiTrpcClient["assets"]["getEditorSuggestions"]["query"]
>[0];

export type EditorAssetSuggestionsOutput = Awaited<
  ReturnType<PlatformApiTrpcClient["assets"]["getEditorSuggestions"]["query"]>
>;

export type EditorAssetSuggestionItem =
  EditorAssetSuggestionsOutput["popular"][number];

export type BacktestStrategyInput = Parameters<
  PlatformApiTrpcClient["strategyInstances"]["getBacktestDataForStrategy"]["query"]
>[0];

export type BacktestStrategyOutput = Awaited<
  ReturnType<
    PlatformApiTrpcClient["strategyInstances"]["getBacktestDataForStrategy"]["query"]
  >
>;

export interface PlatformApiTrpcAdapterOptions {
  authorization?: string | null;
  baseUrl?: string;
  batch?: boolean;
  cookie?: string | null;
  enableSubscriptions?: boolean;
  fetch?: typeof fetch;
  headers?: () => HeaderMap | Promise<HeaderMap>;
  requestTimeoutMs?: number;
  vmPreference?: VmPreference;
  walletAddress?: string | null;
}

export interface BrowserPlatformApiTrpcAdapterOptions {
  authRuntime?: {
    getAuthHeaders: () => Promise<HeaderMap>;
    getSnapshot: () => AppAuthTransportSnapshot;
  };
  baseUrl?: string;
  batch?: boolean;
  enableSubscriptions?: boolean;
  fetch?: typeof fetch;
  getVmPreference?: () => VmPreference | Promise<VmPreference>;
  getWalletAddress?: () =>
    | string
    | null
    | undefined
    | Promise<string | null | undefined>;
  headers?: () => HeaderMap | Promise<HeaderMap>;
  requestTimeoutMs?: number;
  walletAddress?: string | null;
}

function getNonEmptyValue(
  value: string | null | undefined,
): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function toVmPreferenceHeader(
  vmPreference: VmPreference | undefined,
): VmId | undefined {
  if (!vmPreference || vmPreference === "auto") {
    return undefined;
  }

  return vmPreference;
}

function mergeHeaders(
  baseHeaders: HeadersInit | undefined,
  nextHeaders: HeaderMap,
): HeaderMap {
  const merged = new Headers(baseHeaders);

  for (const [key, value] of Object.entries(nextHeaders)) {
    merged.set(key, value);
  }

  const result: HeaderMap = {};
  merged.forEach((value, key) => {
    result[key] = value;
  });

  return result;
}

function createClient(options: {
  baseUrl: string;
  batch: boolean;
  enableSubscriptions: boolean;
  fetch?: typeof fetch;
  headers?: () => HeaderMap | Promise<HeaderMap>;
  requestTimeoutMs: number;
}): PlatformApiTrpcClient {
  const fetchImpl = options.fetch ?? fetch;

  const performRequest = async (
    url: RequestInfo | URL,
    requestOptions?: RequestInit,
    classifyError?: (error: unknown) => {
      errorMessage?: string;
      recordFailure: boolean;
      rethrow: unknown;
    } | null,
  ): Promise<Response> => {
    const requestUrl = getRequestUrl(url);
    const requestCorrelationId = requestOptions?.headers
      ? new Headers(requestOptions.headers).get("x-correlation-id")
      : null;
    try {
      const response = await fetchImpl(requestUrl, {
        ...requestOptions,
        credentials: "include",
      });
      recordPlatformApiResponseMetadata({
        correlationId:
          response.headers.get("x-correlation-id") ?? requestCorrelationId,
        requestId: response.headers.get("x-request-id"),
        routeVisitId: getRouteVisitId(),
        status: response.status,
        url: requestUrl,
      });
      if (response.status >= 400) {
        trackPlatformApiFailure({
          correlationId:
            response.headers.get("x-correlation-id") ?? requestCorrelationId,
          requestId: response.headers.get("x-request-id"),
          routeVisitId: getRouteVisitId(),
          status: response.status,
          url: requestUrl,
        });
      }
      return response;
    } catch (error) {
      const failureHandling = classifyError?.(error);

      if (failureHandling?.recordFailure ?? true) {
        const errorMessage =
          failureHandling?.errorMessage ??
          (error instanceof Error ? error.message : String(error));
        recordPlatformApiRequestFailure({
          correlationId: requestCorrelationId,
          errorMessage,
          routeVisitId: getRouteVisitId(),
          url: requestUrl,
        });
        trackPlatformApiFailure({
          correlationId: requestCorrelationId,
          errorMessage,
          routeVisitId: getRouteVisitId(),
          url: requestUrl,
        });
      }

      throw failureHandling?.rethrow ?? error;
    }
  };

  const withTimeout = async (
    url: RequestInfo | URL,
    requestOptions?: RequestInit,
  ): Promise<Response> => {
    const requestTimeoutMs = Math.max(0, options.requestTimeoutMs);
    const upstreamSignal = requestOptions?.signal;

    if (requestTimeoutMs === 0 && !upstreamSignal) {
      return await performRequest(url, requestOptions);
    }

    const abortController = new AbortController();
    const abortError = createPlatformApiAbortError();
    const timeoutError = createPlatformApiRequestTimeoutError(requestTimeoutMs);
    let abortReason: "timeout" | "upstream" | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const abortFromUpstream = () => {
      if (abortReason) {
        return;
      }

      abortReason = "upstream";
      abortController.abort();
    };

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (upstreamSignal) {
        upstreamSignal.removeEventListener("abort", abortFromUpstream);
      }
    };

    if (upstreamSignal?.aborted) {
      abortFromUpstream();
      throw abortError;
    }

    if (upstreamSignal) {
      upstreamSignal.addEventListener("abort", abortFromUpstream, {
        once: true,
      });
    }

    if (requestTimeoutMs > 0) {
      timeoutId = setTimeout(() => {
        if (abortReason) {
          return;
        }

        abortReason = "timeout";
        abortController.abort();
      }, requestTimeoutMs);
    }

    try {
      return await performRequest(
        url,
        {
          ...requestOptions,
          signal: abortController.signal,
        },
        (error) => {
          if (!isAbortError(error)) {
            return null;
          }

          if (abortReason === "upstream") {
            return {
              recordFailure: false,
              rethrow: abortError,
            };
          }

          if (abortReason === "timeout") {
            return {
              errorMessage: timeoutError.message,
              recordFailure: true,
              rethrow: timeoutError,
            };
          }

          return null;
        },
      );
    } finally {
      cleanup();
    }
  };

  const linkConfig = {
    url: `${options.baseUrl}/v1/trpc`,
    headers: options.headers,
    maxURLLength: PLATFORM_API_TRPC_MAX_URL_LENGTH,
    transformer: superjson,
    fetch(url: RequestInfo | URL, requestOptions?: RequestInit) {
      return withTimeout(url, requestOptions);
    },
  };

  // Batched clients still send out-of-band procedures on their own request
  // so a slow lane cannot hold up a fast one.
  const buildRequestLink = () =>
    options.batch
      ? splitLink({
          condition: (operation) => isOutOfBandProcedurePath(operation.path),
          true: httpLink(linkConfig),
          false: httpBatchLink(linkConfig),
        })
      : httpLink(linkConfig);

  if (options.enableSubscriptions) {
    const link = splitLink({
      condition: (operation) => operation.type === "subscription",
      true: httpSubscriptionLink({
        EventSource: getEventSourceConstructor(),
        eventSourceOptions: {
          fetch: async (url, requestOptions) => {
            const resolvedHeaders = (await options.headers?.()) ?? {};
            return withTimeout(url, {
              ...requestOptions,
              credentials: "include",
              headers: mergeHeaders(requestOptions?.headers, resolvedHeaders),
            });
          },
          withCredentials: true,
        },
        url: `${options.baseUrl}/v1/trpc`,
        transformer: superjson,
      }),
      false: buildRequestLink(),
    });

    return createTRPCClient<AppRouter>({
      links: [link],
    });
  }

  const link = buildRequestLink();

  return createTRPCClient<AppRouter>({
    links: [link],
  });
}


export function createPlatformApiTrpcAdapter(
  options: PlatformApiTrpcAdapterOptions = {},
): PlatformApiTrpcClient {
  const authorization = getNonEmptyValue(options.authorization);
  const cookie = getNonEmptyValue(options.cookie);
  const walletAddress = getNonEmptyValue(options.walletAddress);
  const vmPreferenceHeader = toVmPreferenceHeader(options.vmPreference);
  const chainPreference = toPlatformChainPreference(vmPreferenceHeader);
  const hasHeaderFactory = typeof options.headers === "function";

  const headers =
    authorization ||
    cookie ||
    walletAddress ||
    vmPreferenceHeader ||
    chainPreference ||
    hasHeaderFactory
      ? async (): Promise<HeaderMap> => {
          const baseHeaders: HeaderMap = {};

          if (authorization) {
            baseHeaders.Authorization = authorization;
          }

          if (cookie) {
            baseHeaders.cookie = cookie;
          }

          if (vmPreferenceHeader) {
            baseHeaders["x-vm-preference"] = vmPreferenceHeader;
          }

          if (chainPreference) {
            baseHeaders["x-chain-preference"] = chainPreference;
          }

          if (walletAddress) {
            baseHeaders["x-wallet-address"] = walletAddress;
          }

          const extraHeaders = (await options.headers?.()) ?? {};
          return { ...baseHeaders, ...extraHeaders };
        }
      : undefined;

  return wrapTrpcClientForReact(
    createClient({
      baseUrl: options.baseUrl ?? getPlatformApiBaseUrl(),
      fetch: options.fetch,
      headers,
      batch: options.batch ?? true,
      enableSubscriptions: options.enableSubscriptions ?? false,
      requestTimeoutMs: options.requestTimeoutMs ?? 20_000,
    }),
  );
}

export function createPlatformApiTrpcAdapterFromRequest(
  request: Request,
  options: Omit<PlatformApiTrpcAdapterOptions, "authorization" | "cookie"> = {},
): PlatformApiTrpcClient {
  return createPlatformApiTrpcAdapter({
    ...options,
    authorization: request.headers.get("authorization"),
    cookie: request.headers.get("cookie"),
    walletAddress:
      options.walletAddress ?? request.headers.get("x-wallet-address"),
  });
}

export function createBrowserPlatformApiTrpcAdapter(
  options: BrowserPlatformApiTrpcAdapterOptions = {},
): PlatformApiTrpcClient {
  const authRuntime = options.authRuntime ?? {
    getAuthHeaders: getAppAuthTransportAuthHeaders,
    getSnapshot: getAppAuthTransportSnapshot,
  };

  return createPlatformApiTrpcAdapter({
    baseUrl: options.baseUrl,
    batch: options.batch ?? false,
    enableSubscriptions: options.enableSubscriptions,
    fetch: options.fetch,
    requestTimeoutMs: options.requestTimeoutMs,
    headers: async () => {
      const authHeaders = await authRuntime.getAuthHeaders();
      const appAuthTransportSnapshot = authRuntime.getSnapshot();
      const vmPreference = await options.getVmPreference?.();
      const vmPreferenceHeader = toVmPreferenceHeader(vmPreference);
      const chainPreference = toPlatformChainPreference(vmPreferenceHeader);
      const walletAddress = getNonEmptyValue(
        (await options.getWalletAddress?.()) ??
          options.walletAddress ??
          appAuthTransportSnapshot.walletAddress,
      );

      const baseHeaders: HeaderMap = { ...authHeaders };
      if (vmPreferenceHeader) {
        baseHeaders["x-vm-preference"] = vmPreferenceHeader;
      }
      if (chainPreference) {
        baseHeaders["x-chain-preference"] = chainPreference;
      }
      if (walletAddress) {
        baseHeaders["x-wallet-address"] = walletAddress;
      }

      const additionalHeaders = (await options.headers?.()) ?? {};
      return {
        ...baseHeaders,
        "x-correlation-id": createCorrelationId(),
        ...additionalHeaders,
      };
    },
  });
}
